import { useEffect, useState } from "react";

/**
 * Route loading fallback.
 *
 * Speed Index optimization: we DON'T paint the skeleton immediately.
 *  - On first page load, the lazy route chunk usually resolves in <150 ms,
 *    so painting a skeleton just to replace it with real content inflates
 *    Speed Index (the browser paints "content" twice).
 *  - On client-side navigations, fast chunks (cached / prefetched) also
 *    resolve almost instantly — no need to flash a skeleton.
 *
 * We wait 180 ms before showing anything. If the chunk loads first,
 * the user sees the previous page (thanks to v7_startTransition) and then
 * the new page — no skeleton frame, no Speed Index penalty.
 * If loading takes longer, the skeleton appears so the user gets feedback.
 *
 * The wrapper still reserves min-height so the footer never jumps.
 */
const RouteFallback = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 180);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{ minHeight: "calc(100vh - 80px)" }}
      className="pt-20 sm:pt-24"
    >
      {show && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6 animate-pulse">
          <div className="h-8 w-1/3 bg-muted/60 rounded-sm" />
          <div className="h-4 w-2/3 bg-muted/40 rounded-sm" />
          <div className="h-4 w-1/2 bg-muted/40 rounded-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[4/3] bg-muted/40 rounded-sm" />
                <div className="h-4 w-3/4 bg-muted/40 rounded-sm" />
                <div className="h-3 w-1/2 bg-muted/30 rounded-sm" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteFallback;
